import { useState, useCallback, useRef, useEffect } from 'react';
import { sendVideoProgressEvent } from '@/utils/precon-supporter/analytics';

/** GA計測用オプション */
interface GATrackingOptions {
  /** 動画URL */
  videoUrl: string;
  /** 学習進捗ID（同一ユーザー追跡用） */
  progressId?: string;
  /** 完了済み講座IDのカンマ区切り文字列 */
  completedLectures?: string;
}

/**
 * 講座完了処理のロジックをカプセル化するカスタムフック
 *
 * @param lectureId - 講座ID
 * @param completeLectureFn - 講座完了処理の関数
 * @param gaOptions - GA計測用オプション
 * @returns 完了状態とハンドラー
 */
export const useLectureCompletion = (
  lectureId: string,
  completeLectureFn: (lectureId: string) => Promise<void>,
  gaOptions?: GATrackingOptions
) => {
  const [hasCompletedVideo, setHasCompletedVideo] = useState(false);

  // 最新の値をrefで保持して、関数の再生成を防ぐ
  const completeLectureRef = useRef(completeLectureFn);

  useEffect(() => {
    completeLectureRef.current = completeLectureFn;
  }, [completeLectureFn]);

  const handleComplete = useCallback(() => {
    // 動画視聴完了時に呼ばれる
    if (!hasCompletedVideo) {
      setHasCompletedVideo(true);
      completeLectureRef
        .current(lectureId)
        .then(() => {
          // IndexedDB保存成功時のGA計測
          if (gaOptions?.videoUrl) {
            sendVideoProgressEvent({
              step: 'save_success',
              video_url: gaOptions.videoUrl,
              progress_id: gaOptions.progressId,
              completed_lectures: gaOptions.completedLectures || 'none',
            });
          }
        })
        .catch(error => {
          console.error('講座の完了処理に失敗しました:', error);
          // IndexedDB保存失敗時のGA計測
          if (gaOptions?.videoUrl) {
            sendVideoProgressEvent({
              step: 'save_failed',
              video_url: gaOptions.videoUrl,
              progress_id: gaOptions.progressId,
              completed_lectures: gaOptions.completedLectures || 'none',
              error_message:
                error instanceof Error ? error.message : String(error),
            });
          }
          // ユーザーに通知
          alert(
            '講座の視聴完了の記録に失敗しました。\n' +
              'ブラウザの設定でストレージが無効になっていないか確認してください。\n' +
              'この問題が解決しない場合は、ページを再読み込みして動画を最後まで視聴し直してください。'
          );
          // エラー時はフラグを戻す（再試行可能にする）
          setHasCompletedVideo(false);
        });
    }
  }, [lectureId, hasCompletedVideo]);

  return {
    hasCompletedVideo,
    handleComplete,
  };
};
